import { Component, OnInit, AfterViewInit, ViewChild } from '@angular/core';
import { trigger, state, style } from '@angular/animations';
import { MatTableDataSource } from '@angular/material/table';
import { MatPaginator } from '@angular/material/paginator';
import { ActivatedRoute } from '@angular/router';

import { DatatableBaseComponent } from '@components/datatable-base.component';
import { UiService } from '@services/ui.service';
import { SiteGroupsService } from '@services/site-groups.service';
import { SitesService } from '@services/sites.service';
import { Site } from '@classes/site';
import { SiteGroup } from '@classes/site-group';

@Component({
  selector: 'app-site-groups',
  templateUrl: './site-groups.component.html',
  styleUrls: ['./site-groups.component.scss'],
  animations: [
    trigger('detailExpand', [
      state(
        'collapsed',
        style({ height: '0px', minHeight: '0', display: 'none' }),
      ),
      state('expanded', style({ height: '*' })),
    ]),
  ],
})
export class SiteGroupsComponent
  extends DatatableBaseComponent
  implements OnInit, AfterViewInit
{
  siteGroups: SiteGroup[];
  sites: Site[] = [];

  readonly maxDisplayedMembers = 4;
  private expandedSiteGroupIds: string[] = [];

  displayedColumns = ['name', 'sites', 'actions'];
  dataSource: MatTableDataSource<SiteGroup>;

  @ViewChild(MatPaginator) paginator: MatPaginator;

  constructor(
    protected uiSrv: UiService,
    private route: ActivatedRoute,
    private siteGroupsSrv: SiteGroupsService,
    private sitesSrv: SitesService,
  ) {
    super(uiSrv);
  }

  ngOnInit() {
    super.ngOnInit();
    this.getRouteData();
    this.initSubscriptions();
  }

  ngAfterViewInit() {
    this.dataSource.paginator = this.paginator;
  }

  getMemberNames(group: SiteGroup): string[] {
    return (group.sites || []).map((site) => site.name);
  }

  getDisplayedMembers(group: SiteGroup): string[] {
    if (this.isSitesExpanded(group)) {
      return this.getMemberNames(group);
    }

    return this.getMemberNames(group).slice(0, this.maxDisplayedMembers);
  }

  getRemainingMembersCount(group: SiteGroup): number {
    return Math.max(
      this.getMemberNames(group).length - this.maxDisplayedMembers,
      0,
    );
  }

  isSitesExpanded(group: SiteGroup): boolean {
    return this.expandedSiteGroupIds.includes(group.id);
  }

  toggleSites(group: SiteGroup): void {
    const index = this.expandedSiteGroupIds.indexOf(group.id);

    if (index === -1) {
      this.expandedSiteGroupIds.push(group.id);
    } else {
      this.expandedSiteGroupIds.splice(index, 1);
    }
  }

  delete(idSiteGroup: string) {
    const confirmation = confirm(
      'Êtes-vous certain de vouloir supprimer ce groupe de donneurs d\'ordre ?',
    );

    if (confirmation) {
      this.siteGroupsSrv.delete(idSiteGroup).subscribe(() => {
        this.uiSrv.showSnackbar('repository.siteGroups.list.deleted', true);
      });
    }
  }

  applyFilter(event: Event) {
    const filterValue = (event.target as HTMLInputElement).value;
    this.dataSource.filter = filterValue.trim().toLowerCase();
  }

  private initTable(): void {
    this.dataSource = new MatTableDataSource(this.siteGroups);
    this.dataSource.sortingDataAccessor = (item, property) => {
      if (property === 'sites') {
        return this.getMemberNames(item).join(', ');
      }
      return item[property];
    };
    this.dataSource.filterPredicate = (data, filter: string) => {
      const dataStr = `${data.name} ${this.getMemberNames(data).join(
        ' ',
      )}`.toLowerCase();
      return dataStr.includes(filter.trim().toLowerCase());
    };
    this.dataSource.sort = this.sort;
  }

  private getRouteData(): void {
    this.route.data.subscribe(
      (data: { siteGroups: SiteGroup[]; sites: Site[] }) => {
        this.setSites(data.sites);
        this.siteGroups = data.siteGroups || [];
        this.initTable();
      },
    );
  }

  private initSubscriptions(): void {
    this.addSubscription(
      this.siteGroupsSrv.siteGroupsListChanged.subscribe((siteGroups) => {
        this.dataSource.data = siteGroups || [];
        this.dataSource.sort = this.sort;
        this.onEditFinished();
      }),
    );

    this.addSubscription(
      this.sitesSrv.sitesListChanged.subscribe((sites) => {
        this.setSites(sites);
      }),
    );
  }

  private setSites(sites: Site[]): void {
    this.sites = sites || [];
  }
}
